import { useCallback, useEffect, useMemo, useState } from "react";
import { FolderKanban, LogOut, MoreVertical, Plus, Users } from "lucide-react";
import {
  changeWebBuilderUserPassword,
  createWebBuilderUser,
  deleteWebBuilderUser,
  listWebBuilderUsers,
  updateWebBuilderUser
} from "./webBuilderApi";

const ROLE_OPTIONS = [
  { value: "creator", label: "Creator" },
  { value: "mod", label: "Mod" },
  { value: "admin", label: "Admin" }
];

const ROLE_ORDER = {
  admin: 0,
  mod: 1,
  creator: 2
};

const EMPTY_CREATE_FORM = {
  username: "",
  display_name: "",
  game_friendly_id: "",
  password: "",
  max_levels: 50,
  role: "creator"
};

const EMPTY_EDIT_FORM = {
  username: "",
  display_name: "",
  game_friendly_id: "",
  max_levels: 50,
  role: "creator"
};

const SECTION_USERS = "users";
const ROUTE_MANAGE_USERS = "/manage";
const ROUTE_MANAGE_LEVELS = "/manager/levels";

const inputClass =
  "w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm !text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-slate-500 focus:ring-2 focus:ring-slate-200";

const btnPrimary =
  "manage-btn inline-flex items-center justify-center rounded-md border !border-lime-500 !bg-lime-400 px-3 py-2 text-sm font-semibold !text-slate-900 transition hover:!bg-lime-300 disabled:cursor-not-allowed disabled:!opacity-100 disabled:!bg-slate-200 disabled:!border-slate-300 disabled:!text-slate-600";
const btnSecondary =
  "manage-btn inline-flex items-center justify-center rounded-md border !border-sky-400 !bg-sky-100 px-3 py-2 text-sm font-semibold !text-slate-800 transition hover:!bg-sky-50 disabled:cursor-not-allowed disabled:!opacity-100 disabled:!bg-slate-100 disabled:!border-slate-300 disabled:!text-slate-500";
const btnDanger =
  "manage-btn inline-flex items-center justify-center rounded-md border !border-rose-400 !bg-rose-100 px-3 py-2 text-sm font-semibold !text-rose-800 transition hover:!bg-rose-50 disabled:cursor-not-allowed disabled:!opacity-100 disabled:!bg-slate-100 disabled:!border-slate-300 disabled:!text-slate-500";

function roleLabel(role) {
  return ROLE_OPTIONS.find((option) => option.value === role)?.label || role;
}

function SectionCard({ title, subtitle, action, children }) {
  return (
    <section className="rounded-[12px] border border-slate-200 bg-white shadow-sm">
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
        <div>
          <h2 className="text-base font-semibold text-slate-900">{title}</h2>
          {subtitle ? <p className="mt-1 text-sm text-slate-500">{subtitle}</p> : null}
        </div>
        {action}
      </header>
      <div className="px-5 py-4">{children}</div>
    </section>
  );
}

export default function ManagePage({ token, currentUser, onLogout, currentRoute, onNavigate }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState("");
  const [createForm, setCreateForm] = useState(EMPTY_CREATE_FORM);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editingUserId, setEditingUserId] = useState("");
  const [editForm, setEditForm] = useState(EMPTY_EDIT_FORM);
  const [passwordDraft, setPasswordDraft] = useState("");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [openUserMenuId, setOpenUserMenuId] = useState("");

  const isAdmin = currentUser?.role === "admin";
  const isMod = currentUser?.role === "mod";
  const canManageRoles = isAdmin;
  const canDeleteUsers = isAdmin;
  const canCreateUsers = isAdmin;

  const navItems = useMemo(() => {
    const items = [{ key: SECTION_USERS, label: "Users", icon: Users, route: ROUTE_MANAGE_USERS }];
    if (isAdmin) {
      items.push({ key: "levels", label: "Levels", icon: FolderKanban, route: ROUTE_MANAGE_LEVELS });
    }
    return items;
  }, [isAdmin]);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const payload = await listWebBuilderUsers(token);
      setUsers(payload || []);
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    if (!openUserMenuId) return;

    function handleDocumentClick(event) {
      const target = event.target;
      if (!(target instanceof Element)) {
        setOpenUserMenuId("");
        return;
      }
      if (target.closest("[data-user-row-menu='true']")) {
        return;
      }
      setOpenUserMenuId("");
    }

    function handleEscape(event) {
      if (event.key === "Escape") {
        setOpenUserMenuId("");
      }
    }

    document.addEventListener("mousedown", handleDocumentClick);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleDocumentClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [openUserMenuId]);

  const sortedUsers = useMemo(
    () =>
      [...users].sort((left, right) => {
        const leftOrder = ROLE_ORDER[left.role] ?? 99;
        const rightOrder = ROLE_ORDER[right.role] ?? 99;
        if (leftOrder !== rightOrder) {
          return leftOrder - rightOrder;
        }
        return left.username.localeCompare(right.username);
      }),
    [users]
  );

  const editingUser = useMemo(
    () => users.find((user) => user.id === editingUserId) || null,
    [editingUserId, users]
  );
  const modalBusy = editingUser ? busyId === editingUser.id : false;
  const isEditingSelf = useMemo(() => {
    if (!editingUser || !currentUser?.id) return false;
    return editingUser.id === currentUser.id;
  }, [currentUser?.id, editingUser]);
  const isFriendIdLocked = Boolean(editingUser?.is_self_registered);
  const isEditingAdmin = editingUser?.role === "admin";
  const canModManageTarget = !isMod || !isEditingAdmin;
  const canEditProfile = isAdmin;
  const canChangeManagedPassword = !modalBusy && Boolean(editingUser) && canModManageTarget;
  const canToggleBlocked = !modalBusy && !isEditingSelf && canModManageTarget;

  useEffect(() => {
    if (editingUserId && !editingUser) {
      setEditingUserId("");
      setEditForm(EMPTY_EDIT_FORM);
      setPasswordDraft("");
    }
  }, [editingUser, editingUserId]);

  async function handleCreateUser() {
    if (!canCreateUsers) return;
    setCreating(true);
    setError("");
    try {
      await createWebBuilderUser(token, {
        username: createForm.username,
        display_name: createForm.display_name || null,
        game_friendly_id: createForm.game_friendly_id || null,
        password: createForm.password,
        max_levels: Number(createForm.max_levels) || 50,
        role: canManageRoles ? createForm.role : "creator",
        is_blocked: false
      });
      setCreateForm(EMPTY_CREATE_FORM);
      setIsCreateModalOpen(false);
      await loadUsers();
    } catch (createError) {
      setError(createError.message);
    } finally {
      setCreating(false);
    }
  }

  function openCreateModal() {
    if (!canCreateUsers) return;
    setCreateForm(EMPTY_CREATE_FORM);
    setIsCreateModalOpen(true);
  }

  function closeCreateModal() {
    if (creating) return;
    setIsCreateModalOpen(false);
  }

  function startEdit(user) {
    setOpenUserMenuId("");
    setEditingUserId(user.id);
    setEditForm({
      username: user.username,
      display_name: user.display_name || "",
      game_friendly_id: user.game_friendly_id || "",
      max_levels: user.max_levels,
      role: user.role || "creator"
    });
    setPasswordDraft("");
  }

  function closeEditModal() {
    setEditingUserId("");
    setEditForm(EMPTY_EDIT_FORM);
    setPasswordDraft("");
  }

  async function saveEdit() {
    if (!editingUser || !canEditProfile) return;
    setBusyId(editingUser.id);
    setError("");
    try {
      const payload = {
        username: editForm.username,
        display_name: editForm.display_name || null,
        max_levels: Number(editForm.max_levels) || 1
      };
      if (!isFriendIdLocked) {
        payload.game_friendly_id = editForm.game_friendly_id || null;
      }
      if (canManageRoles) {
        payload.role = editForm.role;
      }
      await updateWebBuilderUser(token, editingUser.id, payload);
      await loadUsers();
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setBusyId("");
    }
  }

  async function toggleBlocked() {
    if (!editingUser || !canToggleBlocked) return;
    setBusyId(editingUser.id);
    setError("");
    try {
      await updateWebBuilderUser(token, editingUser.id, {
        is_blocked: !editingUser.is_blocked
      });
      await loadUsers();
    } catch (toggleError) {
      setError(toggleError.message);
    } finally {
      setBusyId("");
    }
  }

  async function changePassword() {
    if (!editingUser || !passwordDraft.trim() || !canChangeManagedPassword) return;
    setBusyId(editingUser.id);
    setError("");
    try {
      await changeWebBuilderUserPassword(token, editingUser.id, passwordDraft.trim());
      setPasswordDraft("");
    } catch (passwordError) {
      setError(passwordError.message);
    } finally {
      setBusyId("");
    }
  }

  async function removeUser() {
    if (!editingUser) return;
    const confirmed = window.confirm(`Delete user ${editingUser.username}?`);
    if (!confirmed) return;

    setBusyId(editingUser.id);
    setError("");
    try {
      await deleteWebBuilderUser(token, editingUser.id);
      closeEditModal();
      await loadUsers();
    } catch (deleteError) {
      setError(deleteError.message);
    } finally {
      setBusyId("");
    }
  }

  return (
    <main className="manage-tailwind manage-layout-shell bg-slate-100 font-sans text-slate-900">
      <div className="flex h-full min-h-0 bg-slate-100">
        <aside
          className={`manage-sidebar relative hidden border-r border-slate-800 bg-slate-900 text-slate-100 transition-all duration-200 md:flex md:flex-col ${
            sidebarCollapsed ? "md:w-20" : "md:w-64"
          }`}
        >
          <div className="flex items-center justify-between border-b border-slate-800 px-3 py-4">
            <div className={`overflow-hidden transition-all ${sidebarCollapsed ? "w-0" : "w-full"}`}>
              <p className="truncate text-sm font-semibold text-white">Builder Manager</p>
              <p className="truncate text-xs text-slate-400">{currentUser.username}</p>
            </div>
            <button
              type="button"
              className="manage-btn manage-sidebar-btn inline-flex h-8 w-8 items-center justify-center rounded-md border transition"
              onClick={() => setSidebarCollapsed((previous) => !previous)}
              aria-label="Toggle sidebar"
            >
              <span className="manage-hamburger-glyph" aria-hidden="true">
                ☰
              </span>
            </button>
          </div>

          <nav className="manage-sidebar-nav flex-1 space-y-1 p-3">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = currentRoute === item.route;
              return (
                <button
                  key={item.key}
                  type="button"
                  className={`manage-btn manage-sidebar-btn flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-semibold transition ${
                    isActive ? "is-active" : ""
                  }`}
                  onClick={() => onNavigate?.(item.route)}
                >
                  <Icon size={16} className="shrink-0" />
                  {!sidebarCollapsed ? <span>{item.label}</span> : null}
                </button>
              );
            })}
          </nav>

          <div className="shrink-0 border-t border-slate-800 p-3">
            <button type="button" className={`${btnSecondary} w-full`} onClick={() => onLogout?.()}>
              <LogOut size={16} className="mr-2" />
              {!sidebarCollapsed ? "Logout" : ""}
            </button>
          </div>
        </aside>

        <section className="manage-content-scroll flex-1 bg-slate-100 p-4 md:p-6">
          <div className="mx-auto flex w-full max-w-7xl flex-col gap-4">
            <header className="rounded-[12px] border border-slate-200 bg-white px-5 py-4 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h1 className="text-lg font-semibold text-slate-900">Manage Builder Workspace</h1>
                  <p className="text-sm text-slate-500">Signed in as {roleLabel(currentUser.role)}</p>
                </div>
                <div className="flex items-center gap-2">
                  {canCreateUsers ? (
                    <button type="button" className={btnPrimary} onClick={openCreateModal}>
                      <Plus size={16} className="mr-1.5" />
                      Create User
                    </button>
                  ) : null}
                  <button type="button" className={`${btnSecondary} md:hidden`} onClick={() => onLogout?.()}>
                    <LogOut size={16} className="mr-1.5" />
                    Logout
                  </button>
                </div>
              </div>
            </header>

            <SectionCard
              title="Manage Builder Users"
              subtitle={
                isAdmin
                  ? "Admin can fully manage users."
                  : "Mods can only block/unblock non-admin users and set passwords for non-admin users."
              }
            >
              {error ? (
                <p className="mb-3 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>
              ) : null}
              {loading ? (
                <p className="text-sm text-slate-500">Loading users...</p>
              ) : (
                <div className="overflow-x-auto md:overflow-visible rounded-lg border border-slate-200">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-4 py-3">Username</th>
                        <th className="px-4 py-3">Name</th>
                        <th className="px-4 py-3">Role</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3">Friend ID</th>
                        <th className="px-4 py-3">Max Levels</th>
                        <th className="px-4 py-3 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedUsers.map((user) => {
                        const busy = busyId === user.id;
                        return (
                          <tr key={user.id} className="border-t border-slate-200 transition-colors hover:bg-slate-50/80">
                            <td className="px-4 py-3 font-medium text-slate-900">{user.username}</td>
                            <td className="px-4 py-3 text-slate-600">{user.display_name || "-"}</td>
                            <td className="px-4 py-3 text-slate-600">{roleLabel(user.role)}</td>
                            <td className="px-4 py-3">
                              <span
                                className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                                  user.is_blocked ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"
                                }`}
                              >
                                {user.is_blocked ? "Blocked" : "Active"}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-slate-600">{user.game_friendly_id || "-"}</td>
                            <td className="px-4 py-3 text-slate-600">{user.max_levels}</td>
                            <td className="px-4 py-3 text-right">
                              <div className="relative inline-flex" data-user-row-menu="true">
                                <button
                                  type="button"
                                  className="manage-btn manage-action-btn inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-300 bg-white !text-slate-800 transition hover:bg-slate-100"
                                  onClick={() => setOpenUserMenuId((previous) => (previous === user.id ? "" : user.id))}
                                  disabled={busy}
                                  aria-label={`Open actions for ${user.username}`}
                                >
                                  <MoreVertical size={18} strokeWidth={2.25} />
                                </button>
                                {openUserMenuId === user.id ? (
                                  <div className="absolute right-0 top-10 z-20 w-44 rounded-md border border-slate-200 bg-white p-1 shadow-lg">
                                    <button
                                      type="button"
                                      className="manage-btn flex w-full items-center rounded px-2 py-2 text-left text-sm !text-slate-700 transition hover:bg-slate-100"
                                      onClick={() => startEdit(user)}
                                    >
                                      <Users size={14} className="mr-2" />
                                      Edit user
                                    </button>
                                  </div>
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </SectionCard>
          </div>
        </section>
      </div>

      {editingUser ? (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-900/55 p-4 backdrop-blur-[2px]"
          onClick={closeEditModal}
        >
          <section
            className="w-full max-w-4xl rounded-[12px] border border-slate-200 bg-white shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-label={`Edit user ${editingUser.username}`}
            onClick={(event) => event.stopPropagation()}
          >
            <header className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Edit User</h3>
                <p className="mt-1 text-sm text-slate-500">
                  Editing: <strong>{editingUser.username}</strong>
                </p>
              </div>
              <button type="button" className={btnSecondary} disabled={modalBusy} onClick={closeEditModal}>
                Close
              </button>
            </header>

            <div className="space-y-5 px-5 py-4">
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                <label className="text-sm text-slate-700">
                  <span className="mb-1 block">Username</span>
                  <input
                    className={inputClass}
                    value={editForm.username}
                    disabled={modalBusy || !canEditProfile}
                    onChange={(event) => setEditForm((previous) => ({ ...previous, username: event.target.value }))}
                  />
                </label>

                <label className="text-sm text-slate-700">
                  <span className="mb-1 block">Display Name</span>
                  <input
                    className={inputClass}
                    value={editForm.display_name}
                    disabled={modalBusy || !canEditProfile}
                    onChange={(event) =>
                      setEditForm((previous) => ({ ...previous, display_name: event.target.value }))
                    }
                  />
                </label>

                <label className="text-sm text-slate-700">
                  <span className="mb-1 block">Game Friendly ID</span>
                  <input
                    className={inputClass}
                    value={editForm.game_friendly_id}
                    disabled={modalBusy || !canEditProfile || isFriendIdLocked}
                    onChange={(event) =>
                      setEditForm((previous) => ({
                        ...previous,
                        game_friendly_id: event.target.value
                      }))
                    }
                  />
                  {isFriendIdLocked ? (
                    <small className="mt-1 block text-xs text-slate-500">
                      Self-registered account. Friend ID cannot be changed.
                    </small>
                  ) : null}
                </label>

                <label className="text-sm text-slate-700">
                  <span className="mb-1 block">Max Levels</span>
                  <input
                    className={inputClass}
                    type="number"
                    min={1}
                    value={editForm.max_levels}
                    disabled={modalBusy || !canEditProfile}
                    onChange={(event) =>
                      setEditForm((previous) => ({
                        ...previous,
                        max_levels: Number(event.target.value) || 1
                      }))
                    }
                  />
                </label>

                {canManageRoles ? (
                  <label className="text-sm text-slate-700">
                    <span className="mb-1 block">Role</span>
                    <select
                      className={inputClass}
                      value={editForm.role}
                      disabled={modalBusy || isEditingSelf}
                      onChange={(event) => setEditForm((previous) => ({ ...previous, role: event.target.value }))}
                    >
                      {ROLE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}
              </div>

              {canEditProfile ? (
                <div className="flex justify-end">
                  <button type="button" className={btnPrimary} disabled={modalBusy} onClick={saveEdit}>
                    Save Profile
                  </button>
                </div>
              ) : null}

              <div className="h-px bg-slate-200" />

              <div className="flex flex-wrap gap-2">
                <button type="button" className={btnSecondary} disabled={!canToggleBlocked} onClick={toggleBlocked}>
                  {editingUser.is_blocked ? "Unblock User" : "Block User"}
                </button>
              </div>

              {isEditingSelf ? (
                <p className="text-sm text-slate-500">
                  You cannot change your own role or block your own account.
                </p>
              ) : null}
              {isMod && isEditingAdmin ? (
                <p className="text-sm text-slate-500">Mods cannot manage admin accounts.</p>
              ) : null}
              {isMod && !isEditingAdmin ? (
                <p className="text-sm text-slate-500">
                  Mods can only block or unblock this user and set a new password.
                </p>
              ) : null}

              <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
                <label className="text-sm text-slate-700">
                  <span className="mb-1 block">New Password</span>
                  <input
                    className={inputClass}
                    type="password"
                    value={passwordDraft}
                    disabled={modalBusy || !canModManageTarget}
                    placeholder="At least 6 characters"
                    onChange={(event) => setPasswordDraft(event.target.value)}
                  />
                </label>
                <button
                  type="button"
                  className={btnSecondary}
                  disabled={!canChangeManagedPassword || !passwordDraft.trim()}
                  onClick={changePassword}
                >
                  Change Password
                </button>
              </div>

              {canDeleteUsers ? (
                <div className="flex justify-end">
                  <button type="button" className={btnDanger} disabled={modalBusy || isEditingSelf} onClick={removeUser}>
                    Delete User
                  </button>
                </div>
              ) : null}
            </div>
          </section>
        </div>
      ) : null}

      {isCreateModalOpen && canCreateUsers ? (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-900/55 p-4 backdrop-blur-[2px]"
          onClick={closeCreateModal}
        >
          <section
            className="w-full max-w-3xl rounded-[12px] border border-slate-200 bg-white shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-label="Create user"
            onClick={(event) => event.stopPropagation()}
          >
            <header className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Create User</h3>
                <p className="mt-1 text-sm text-slate-500">Create a builder account with limits and role.</p>
              </div>
              <button type="button" className={btnSecondary} disabled={creating} onClick={closeCreateModal}>
                Close
              </button>
            </header>

            <div className="space-y-4 px-5 py-4">
              <div className="grid gap-4 md:grid-cols-2">
                <label className="text-sm text-slate-700">
                  <span className="mb-1 block">Username</span>
                  <input
                    className={inputClass}
                    value={createForm.username}
                    onChange={(event) => setCreateForm((previous) => ({ ...previous, username: event.target.value }))}
                  />
                </label>

                <label className="text-sm text-slate-700">
                  <span className="mb-1 block">Display Name</span>
                  <input
                    className={inputClass}
                    value={createForm.display_name}
                    onChange={(event) =>
                      setCreateForm((previous) => ({ ...previous, display_name: event.target.value }))
                    }
                  />
                </label>

                <label className="text-sm text-slate-700">
                  <span className="mb-1 block">Game Friendly ID</span>
                  <input
                    className={inputClass}
                    value={createForm.game_friendly_id}
                    onChange={(event) =>
                      setCreateForm((previous) => ({
                        ...previous,
                        game_friendly_id: event.target.value
                      }))
                    }
                  />
                </label>

                <label className="text-sm text-slate-700">
                  <span className="mb-1 block">Password</span>
                  <input
                    className={inputClass}
                    type="password"
                    value={createForm.password}
                    onChange={(event) => setCreateForm((previous) => ({ ...previous, password: event.target.value }))}
                  />
                </label>

                <label className="text-sm text-slate-700">
                  <span className="mb-1 block">Max Levels</span>
                  <input
                    className={inputClass}
                    type="number"
                    min={1}
                    value={createForm.max_levels}
                    onChange={(event) =>
                      setCreateForm((previous) => ({
                        ...previous,
                        max_levels: Number(event.target.value) || 1
                      }))
                    }
                  />
                </label>

                {canManageRoles ? (
                  <label className="text-sm text-slate-700">
                    <span className="mb-1 block">Role</span>
                    <select
                      className={inputClass}
                      value={createForm.role}
                      onChange={(event) => setCreateForm((previous) => ({ ...previous, role: event.target.value }))}
                    >
                      {ROLE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}
              </div>

              <div className="flex justify-end">
                <button type="button" className={btnPrimary} disabled={creating} onClick={handleCreateUser}>
                  {creating ? "Creating..." : "Create User"}
                </button>
              </div>
            </div>
          </section>
        </div>
      ) : null}
    </main>
  );
}
